import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Link
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { ChevronRight, CalendarCheck, ChevronDown, Info, Plus } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { EXTERNAL_URLS } from '../config/urls';
import { isAdminUser } from '../utils/adminUtils';
import { useTranslation } from 'react-i18next';
import { 
  fetchCountryOptions, 
  fetchPhoneCodes, 
  listCodeList,
  CodeListItem,
  CountryOption,
  NationalityCode,
  // clearCodeListCache // Available if needed for future use 
} from '../services/codeListService';
import {
  getRunnerProfile,
  getPublicUpcomingRegistrations,
  RunnerParticipation,
  RunnerProfile,
  RunnerProfileEditableDetails,
  RunnerProfileUpdateResult,
  RunnerUpcomingRegistration,
  RunnerKutcParticipationData,
  getRunnerKutcParticipationData,
  updateRunnerProfileDetails,
  updateOwnRunnerProfileDetails,
  ensureLanguagePreference
} from '../services/runnerProfileService';
import { getRunnerMoResults, MOResultEntry, getEditionResults } from '../services/moResultsService';
import { useEventEdition } from '../contexts/EventEditionContext';
import { isStripePaymentsEnabled } from '../config/featureFlags';
import { redirectToStripeCheckout } from '../services/stripeService';
import { cancelRegistrationByUser } from '../services/registrationService';
import { submitClaimProfileRequest, submitUnclaimProfileRequest } from '../services/claimProfileService';
import { 
  getFirestore,
  collection,
  query,
  where,
  limit,
  getDocs
} from 'firebase/firestore';

const formatTimeDisplay = (display: string | null | undefined, seconds: number | null | undefined): string => {
  if (display && display.trim().length > 0) {
    return display;
  }
  if (!seconds || seconds <= 0) {
    return '—';
  }
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const parts = [hrs, mins, secs]
    .map((value) => value.toString().padStart(2, '0'))
    .join(':');
  return parts;
};

type TranslateFn = (key: string, options?: any) => string;

const formatDateTimeDisplay = (value: Date | null | undefined, t: TranslateFn): string => {
  if (!value) {
    return t('common.dateTba');
  }
  return value.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const parseYearFromEditionId = (editionId: string): number | null => {
  if (!editionId) return null;
  const m = String(editionId).match(/(\d{4})$/);
  if (!m) return null;
  const y = Number(m[1]);
  return Number.isFinite(y) ? y : null;
};

const formatRank = (value: number | null | undefined): string => {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return `#${value}`;
};

const formatLoops = (loops: number | undefined): string => {
  if (!loops || loops <= 0) {
    return '—';
  }
  return loops.toString();
};

const formatMoClass = (value: string | undefined | null, t: TranslateFn): string => {
  if (!value) return '—';
  const v = String(value).toLowerCase();
  if (v === 'konkurranse') return t('runners.profile.mo.class.competition');
  if (v === 'trim_tidtaking') return t('runners.profile.mo.class.trimTimed');
  if (v === 'turklasse') return t('runners.profile.mo.class.hike');
  if (v === 'volunteer') return t('runners.profile.mo.class.volunteer');
  return value;
};

const isCompetitionClass = (value: string | undefined | null): boolean => {
  if (!value) return false;
  return String(value).toLowerCase() === 'konkurranse';
};

const formatMoStatus = (value: string | undefined | null, t: TranslateFn): string => {
  const v = (value ?? '').toString().trim().toUpperCase();
  if (v === 'DNS') return t('runners.profile.mo.status.dns');
  if (v === 'DNF') return t('runners.profile.mo.status.dnf');
  return t('runners.profile.mo.status.finished');
};

const isCountedMoClass = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const v = String(value).toLowerCase();
  return v === 'konkurranse' || v === 'trim_tidtaking' || v === 'turklasse' || v === 'volunteer';
};

const normalizeNameKey = (value: string | undefined | null): string | null => {
  if (!value) return null;
  const key = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  return key || null;
};

const DEFAULT_CANCELLATION_REASON_CODE = 'noReason';
const SERVICE_PORTAL_URL = 'https://krultra.atlassian.net/servicedesk/customer/portal/1';
const CLOSED_EVENT_STATUSES = new Set(['closed', 'in_progress', 'suspended', 'finished', 'cancelled', 'finalized']);

const getCancellationUnavailableReason = (registration: RunnerUpcomingRegistration, t: TranslateFn): string | null => {
  const registrationStatus = String(registration.status || '').toLowerCase();
  if (registrationStatus !== 'pending' && registrationStatus !== 'confirmed') {
    return t('cancellation.error.notAllowed');
  }

  const eventStatus = String(registration.eventStatus || '').toLowerCase();
  if (eventStatus === 'closed') {
    return t('cancellation.error.eventClosed');
  }
  if (CLOSED_EVENT_STATUSES.has(eventStatus)) {
    return t('cancellation.error.notAllowed');
  }

  const cutoff = registration.cancellationDeadline ?? registration.startTime;
  if (!cutoff || cutoff.getTime() <= Date.now()) {
    return t('cancellation.error.deadlinePassed');
  }

  return null;
};

const formatYears = (years: number[]): string => {
  if (!years || years.length === 0) {
    return '—';
  }
  return years.join(', ');
};

const RunnerProfilePage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const { setEvent } = useEventEdition();
  const { t } = useTranslation();
  const debugTag = '[RunnerProfilePage]';
  const [profile, setProfile] = useState<RunnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [privateProfileLoaded, setPrivateProfileLoaded] = useState(false);
  const [publicUpcomingLoaded, setPublicUpcomingLoaded] = useState(false);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  const [authPersonId, setAuthPersonId] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [newClub, setNewClub] = useState('');
  const clubInputRef = useRef<HTMLInputElement>(null);
  const [editableDetails, setEditableDetails] = useState<RunnerProfileEditableDetails | null>(null);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsSuccess, setDetailsSuccess] = useState(false);
  const [detailsResult, setDetailsResult] = useState<RunnerProfileUpdateResult | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // State for dynamic country options
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  
  // State for dynamic phone codes
  const [phoneCodes, setPhoneCodes] = useState<NationalityCode[]>([]);
  const [isLoadingPhoneCodes, setIsLoadingPhoneCodes] = useState(true);

  const [kutcExpanded, setKutcExpanded] = useState(false);
  const [kutcLoading, setKutcLoading] = useState(false);
  const [kutcError, setKutcError] = useState<string | null>(null);
  const [kutcData, setKutcData] = useState<RunnerKutcParticipationData | null>(null);

  const [moExpanded, setMoExpanded] = useState(false);
  const [moLoaded, setMoLoaded] = useState(false);
  const [moResults, setMoResults] = useState<MOResultEntry[]>([]);
  const [moLoading, setMoLoading] = useState<boolean>(true);
  const [moSummary, setMoSummary] = useState<{
    appearances: number;
    years: number[];
    bestTimeDisplay: string | null;
    bestGenderRank: number | null;
    bestAdjustedDisplay: string | null;
    bestAggRank: number | null;
  } | null>(null);
  const [moRankCache, setMoRankCache] = useState<Record<string, {
    adjustedByName: Record<string, number>;
    byGenderName: Record<string, Record<string, number>>;
  }>>({});

  // Payment state
  const [payingRegistrationId, setPayingRegistrationId] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  // Receipt menu state for multiple payments
  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState<null | HTMLElement>(null);
  const [receiptMenuPayments, setReceiptMenuPayments] = useState<any[]>([]);

  // Registration cancellation state
  const [registrationStatusLabels, setRegistrationStatusLabels] = useState<Record<string, string>>({});
  const [cancellationReasonOptions, setCancellationReasonOptions] = useState<CodeListItem[]>([]);
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [cancellationFinalDialogOpen, setCancellationFinalDialogOpen] = useState(false);
  const [selectedCancellationRegistration, setSelectedCancellationRegistration] = useState<RunnerUpcomingRegistration | null>(null);
  const [selectedCancellationReasonCode, setSelectedCancellationReasonCode] = useState(DEFAULT_CANCELLATION_REASON_CODE);
  const [selectedCancellationReasonText, setSelectedCancellationReasonText] = useState('');
  const [cancellationAcknowledged, setCancellationAcknowledged] = useState(false);
  const [cancellingRegistrationId, setCancellingRegistrationId] = useState<string | null>(null);
  const [cancellationError, setCancellationError] = useState<string | null>(null);
  const [cancellationSuccess, setCancellationSuccess] = useState<string | null>(null);

  // Claim profile state
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [pendingClaimStatus, setPendingClaimStatus] = useState<'claim' | 'unclaim' | null>(null);
  
  // Unclaim confirmation dialog state
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);
  const [keepPersonalDetails, setKeepPersonalDetails] = useState(true);

  // Scroll to top when page loads or personId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [personId]);

  useEffect(() => {
    if (!personId) return;
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getRunnerProfile(personId, {
          includeParticipations: false,
          includeUpcomingRegistrations: false,
          includePrivateDetails: false
        });
        if (!isMounted) return;
        // Redirect to canonical route id (prefer personId) to avoid exposing email-style legacy ids.
        if (personId !== data.routeId) {
          navigate(`/runners/${data.routeId}` as any, { replace: true } as any);
          return;
        }
        setProfile(data);
        setPrivateProfileLoaded(false);
        setPublicUpcomingLoaded(false);
        setKutcData(null);
        setKutcError(null);
        setKutcExpanded(false);
        setMoExpanded(false);
        setMoLoaded(false);
        setMoResults([]);
        setMoSummary(null);
        setMoRankCache({});
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || t('runners.profile.loadFailed'));
          console.warn(debugTag, 'Failed to load profile', { userId: personId, error: err });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [navigate, personId, t]);

  useEffect(() => {
    let isMounted = true;
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!isMounted) {
        return;
      }
      const nextAuthUserId = user?.uid ?? null;
      const nextAuthEmail = user?.email ?? null;
      setAuthUserId(nextAuthUserId);
      setAuthEmail(nextAuthEmail);
      setAuthPersonId(null);
      if (user) {
        let resolvedAdmin = false;
        isAdminUser(user.uid)
          .then((flag) => {
            resolvedAdmin = Boolean(flag);
            if (isMounted) {
              setIsAdmin(resolvedAdmin);
            }
          })
          .finally(() => {
            if (isMounted) {
              setAuthLoading(false);
            }
          })
          .catch(() => {
            if (isMounted) {
              setIsAdmin(false);
              setAuthLoading(false);
              console.warn(debugTag, 'Admin check failed', {
                authUid: nextAuthUserId,
                authEmail: user.email
              });
            }
          });
      } else {
        setIsAdmin(false);
        setAuthLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Fetch country options for nationality dropdown
  useEffect(() => {
    const fetchCountryData = async () => {
      try {
        setIsLoadingCountries(true);
        setIsLoadingPhoneCodes(true);
        
        // Fetch both countries and phone codes in parallel
        const [countries, phoneCodesData] = await Promise.all([
          fetchCountryOptions(),
          fetchPhoneCodes()
        ]);

        // Sort countries alphabetically within groups
        const sortedCountries = countries.sort((a, b) => {
          // For common countries, preserve sortOrder sequence
          if (a.isCommon && b.isCommon) {
            return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
          }
          // Common countries first, then others
          if (a.isCommon && !b.isCommon) return -1;
          if (!a.isCommon && b.isCommon) return 1;
          
          // Alphabetical sort only for "other countries"
          const aName = getCountryDisplayName(a);
          const bName = getCountryDisplayName(b);
          return aName.localeCompare(bName, t('currentLocale', 'en'));
        });

        setCountryOptions(sortedCountries);
        setPhoneCodes(phoneCodesData);
      } catch (error) {
        console.error('Failed to fetch country options:', error);
        // Fallback to empty array - the autocomplete will handle this gracefully
        setCountryOptions([]);
        setPhoneCodes([]);
      } finally {
        setIsLoadingCountries(false);
        setIsLoadingPhoneCodes(false);
      }
    };

    fetchCountryData();
  }, [t]); // getCountryDisplayName is defined later, cannot be in dependencies

  useEffect(() => {
    if (!authUserId) {
      setAuthPersonId(null);
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      try {
        const authProfile = await getRunnerProfile(authUserId, {
          identityMode: 'auth-account',
          includeParticipations: false,
          includeUpcomingRegistrations: false,
          includePrivateDetails: true
        });
        if (!cancelled) {
          setAuthPersonId(authProfile.personId ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setAuthPersonId(null);
        }
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  useEffect(() => {
    if (!moExpanded || !profile?.personId) return;
    if (moLoaded) {
      return;
    }
    let isMounted = true;
    setMoLoading(true);
    getRunnerMoResults(profile.personId as number)
      .then((entries) => {
        if (isMounted) setMoResults(entries);
      })
      .catch(() => {
        if (isMounted) setMoResults([]);
      })
      .finally(() => {
        if (isMounted) {
          setMoLoaded(true);
          setMoLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [profile?.personId, moExpanded, moLoaded]);

  // Build per-edition rank caches for competition entries (time by gender, adjusted overall), by normalized name
  useEffect(() => {
    if (!moExpanded) {
      return;
    }
    let cancelled = false;
    const buildCache = async () => {
      const editions = Array.from(new Set(
        moResults
          .filter((e) => isCompetitionClass(e.class) && e.editionId)
          .map((e) => e.editionId as string)
      ));
      if (editions.length === 0) {
        if (!cancelled) setMoRankCache({});
        return;
      }

      const nextCache: Record<string, {
        adjustedByName: Record<string, number>;
        byGenderName: Record<string, Record<string, number>>;
      }> = {};

      for (const editionId of editions) {
        const entriesThisEdition = moResults.filter((e) => e.editionId === editionId && isCompetitionClass(e.class));
        const genders = Array.from(new Set(entriesThisEdition.map((e) => (e.gender ?? '').toString()).filter(Boolean)));
        const byGenderName: Record<string, Record<string, number>> = {};

        // Gender rank (time)
        await Promise.all(
          genders.map(async (g) => {
            try {
              const list = await getEditionResults(editionId, { classFilter: 'konkurranse', genderFilter: g as any, ranking: 'time' });
              const mapName: Record<string, number> = {};
              list.forEach((it) => {
                const r = typeof it.rankTime === 'number' && Number.isFinite(it.rankTime) ? (it.rankTime as number) : null;
                if (r != null) {
                  const nk = normalizeNameKey(it.fullName);
                  if (nk) {
                    if (!(nk in mapName) || r < mapName[nk]) {
                      mapName[nk] = r;
                    }
                  }
                }
              });
              byGenderName[g] = mapName;
            } catch (err) {
              byGenderName[g] = {};
            }
          })
        );

        // Adjusted rank (AGG overall)
        let adjustedByName: Record<string, number> = {};
        try {
          const listAdj = await getEditionResults(editionId, { classFilter: 'konkurranse', ranking: 'adjusted' });
          const mapName: Record<string, number> = {};
          listAdj.forEach((it) => {
            const r = typeof it.rankAdjusted === 'number' && Number.isFinite(it.rankAdjusted) ? (it.rankAdjusted as number) : null;
            if (r != null) {
              const nk = normalizeNameKey(it.fullName);
              if (nk) {
                if (!(nk in mapName) || r < mapName[nk]) {
                  mapName[nk] = r;
                }
              }
            }
          });
          adjustedByName = mapName;
        } catch (err) {
          adjustedByName = {};
        }

        nextCache[editionId] = { adjustedByName, byGenderName };
      }

      if (!cancelled) setMoRankCache(nextCache);
    };
    buildCache();
    return () => { cancelled = true; };
  }, [moResults, moExpanded]);

  useEffect(() => {
    if (!moExpanded) {
      return;
    }
    let cancelled = false;
    const compute = async () => {
      if (profile?.personId == null) {
        setMoSummary(null);
        return;
      }
      const comp = moResults.filter((e) => isCompetitionClass(e.class));
      const countedAll = moResults.filter((e) => isCountedMoClass(e.class));
      const notDnsAll = countedAll.filter((e) => (e.status ?? '').toString().toUpperCase() !== 'DNS');
      const yearsSet = new Set<number>();
      notDnsAll.forEach((e) => {
        if (typeof e.editionYear === 'number' && Number.isFinite(e.editionYear)) {
          yearsSet.add(e.editionYear);
        } else if (e.editionId) {
          const m = String(e.editionId).match(/(\d{4})$/);
          if (m) yearsSet.add(Number(m[1]));
        }
      });

      const appearances = yearsSet.size;
      const years = Array.from(yearsSet).sort((a, b) => a - b);

      let bestTimeDisplay: string | null = null;
      let bestGenderRank: number | null = null;
      let bestAdjustedDisplay: string | null = null;
      let bestAggRank: number | null = null;

      // Best unadjusted time (competition only)
      const timed = comp
        .filter((e) => (e.timeSeconds ?? null) != null && (e.timeSeconds as number) > 0)
        .slice()
        .sort((a, b) => (a.timeSeconds ?? Number.POSITIVE_INFINITY) - (b.timeSeconds ?? Number.POSITIVE_INFINITY));
      if (timed.length > 0) {
        const best = timed[0];
        bestTimeDisplay = formatTimeDisplay(best.timeDisplay ?? null, best.timeSeconds ?? null);
        // Try to compute gender rank for that edition
        try {
          const gender = (best.gender ?? null) as any;
          if (gender && best.editionId) {
            const editionEntries = await getEditionResults(best.editionId, {
              classFilter: 'konkurranse',
              genderFilter: gender,
              ranking: 'time'
            });
            const match = editionEntries.find((it) =>
              (profile.personId != null && it.personId != null && it.personId === profile.personId) ||
              false
            );
            bestGenderRank = match?.rankTime ?? null;
            if (bestGenderRank == null) {
              // fallback to first index + 1 when name matches closely
              const alt = editionEntries.find((it) => (it.fullName || '').toLowerCase() === (best.fullName || '').toLowerCase());
              bestGenderRank = alt ? alt.rankTime ?? null : null;
            }
          } else {
            bestGenderRank = best.rankTime ?? null;
          }
        } catch {
          bestGenderRank = best.rankTime ?? null;
        }
      }

      // Best adjusted time (competition only)
      const adjusted = comp
        .filter((e) => (e.adjustedSeconds ?? null) != null && (e.adjustedSeconds as number) > 0)
        .slice()
        .sort((a, b) => (a.adjustedSeconds ?? Number.POSITIVE_INFINITY) - (b.adjustedSeconds ?? Number.POSITIVE_INFINITY));
      if (adjusted.length > 0) {
        const bestAdj = adjusted[0];
        bestAdjustedDisplay = formatTimeDisplay(bestAdj.adjustedDisplay ?? null, bestAdj.adjustedSeconds ?? null);
        try {
          if (bestAdj.editionId) {
            const editionEntriesAgg = await getEditionResults(bestAdj.editionId, {
              classFilter: 'konkurranse',
              ranking: 'adjusted'
            });
            const match = editionEntriesAgg.find((it) =>
              (profile.personId != null && it.personId != null && it.personId === profile.personId) ||
              false
            );
            bestAggRank = match?.rankAdjusted ?? null;
            if (bestAggRank == null) {
              const alt = editionEntriesAgg.find((it) => (it.fullName || '').toLowerCase() === (bestAdj.fullName || '').toLowerCase());
              bestAggRank = alt ? alt.rankAdjusted ?? null : null;
            }
          } else {
            bestAggRank = bestAdj.rankAdjusted ?? null;
          }
        } catch {
          bestAggRank = bestAdj.rankAdjusted ?? null;
        }
      }

      if (!cancelled) {
        setMoSummary({ appearances, years, bestTimeDisplay, bestGenderRank, bestAdjustedDisplay, bestAggRank });
      }
    };
    compute();
    return () => { cancelled = true; };
  }, [moResults, profile?.personId, moExpanded]);

  useEffect(() => {
    if (!kutcExpanded || !profile?.routeId || !profile?.personId) {
      return;
    }
    if (kutcData) {
      return;
    }

    let isMounted = true;
    setKutcLoading(true);
    setKutcError(null);
    getRunnerKutcParticipationData(profile.routeId, profile.personId)
      .then((data) => {
        if (isMounted) setKutcData(data);
      })
      .catch((err: any) => {
        if (isMounted) {
          setKutcData(null);
          setKutcError(err?.message || 'Failed to load participation history');
        }
      })
      .finally(() => {
        if (isMounted) setKutcLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [kutcExpanded, profile?.routeId, profile?.personId, kutcData]);

  useEffect(() => {
    if (!profile) {
      setEditableDetails(null);
      return;
    }

    const sanitizedCode = (profile.phoneCountryCode ?? '').replace(/^\+/, '').replace(/\D/g, '');
    const sanitizedPhone = (profile.phone ?? '').replace(/\D/g, '');


    setEditableDetails({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      displayName: profile.displayName ?? '',
      phoneCountryCode: sanitizedCode,
      phone: sanitizedPhone,
      dateOfBirth: profile.dateOfBirth ? 
                      (() => {
                        try {
                          let date;
                          // Handle Firestore Timestamp objects (check for toDate method before Date check)
                          if (profile.dateOfBirth && typeof (profile.dateOfBirth as any).toDate === 'function') {
                            date = (profile.dateOfBirth as any).toDate();
                          } else if (profile.dateOfBirth instanceof Date) {
                            date = profile.dateOfBirth;
                          } else {
                            date = new Date(profile.dateOfBirth);
                          }
                          return date instanceof Date && !isNaN(date.getTime()) ? date : null;
                        } catch {
                          return null;
                        }
                      })() : null,
      yearOfBirth: profile.yearOfBirth ?? null,
      nfifLicenseNumber: profile.nfifLicenseNumber ?? null,
      gender: (profile.gender?.toLowerCase() === 'male' ? 'male' : 
                profile.gender?.toLowerCase() === 'female' ? 'female' : '') as '' | 'male' | 'female',
      nationality: profile.nationality ?? '',
      representing: profile.representing ?? [],
      sendRunningOffers: profile.sendRunningOffers ?? false,
      notifyFutureEvents: profile.notifyFutureEvents ?? false,
      languagePreference: profile.languagePreference ?? null
    });
    setDetailsError(null);
  }, [profile]);

  useEffect(() => {
    let cancelled = false;

    listCodeList('status', 'registrations')
      .then((items) => {
        if (cancelled) return;
        const map = items.reduce<Record<string, string>>((acc, item) => {
          const code = String(item.code || '').trim().toLowerCase();
          if (code) {
            acc[code] = String(item.verboseName || item.code || '').trim();
          }
          return acc;
        }, {});
        setRegistrationStatusLabels(map);
      })
      .catch((err) => {
        console.warn('[RunnerProfilePage] Failed to load registration status codeList labels', err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    listCodeList('cancellationReason', 'registrations')
      .then((items) => {
        if (cancelled) return;

        const normalizedItems = Array.isArray(items) ? items : [];
        setCancellationReasonOptions(normalizedItems);
      })
      .catch((err) => {
        console.warn('[RunnerProfilePage] Failed to load cancellation reason codeList options', err);
        setCancellationReasonOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => {
    if (!profile) {
      return null;
    }
    return {
      name: `${profile.firstName} ${profile.lastName}`.trim(),
      appearances: '—',
      years: '—',
      totalLoops: '—',
      best: '—'
    };
  }, [profile]);

  const kutcStats = useMemo(() => {
    if (!kutcData) {
      return null;
    }
    return {
      appearances: t('runners.profile.kutc.appearancesCount', { count: kutcData.totalAppearances }),
      years: formatYears(kutcData.appearanceYears),
      totalLoops: t('runners.profile.kutc.loopsCount', { count: kutcData.totalLoops }),
      best: kutcData.bestPerformance
        ? t('runners.profile.kutc.bestPerformance', {
            loops: kutcData.bestPerformance.loops,
            time: formatTimeDisplay(kutcData.bestPerformance.totalTimeDisplay, kutcData.bestPerformance.totalTimeSeconds),
            year: kutcData.bestPerformance.year
          })
        : '—'
    };
  }, [kutcData, t]);

  const personIdMatches = Boolean(
    profile?.personId != null && authPersonId != null && profile.personId === authPersonId
  );

  const isOwnProfile = Boolean(profile) && !authLoading && Boolean(authUserId) && (
    authUserId === profile?.userDocId ||
    personIdMatches
  );

  const canViewPrivateDetails = isOwnProfile || isAdmin;
  const canEdit = isOwnProfile || isAdmin;

  useEffect(() => {
    if (!profile || authLoading || !authUserId) {
      return;
    }
    if (canViewPrivateDetails) {
      return;
    }
    if (publicUpcomingLoaded) {
      return;
    }

    if (profile.personId == null) {
      setProfile((prev) => (prev ? { ...prev, upcomingRegistrations: [] } : prev));
      setPublicUpcomingLoaded(true);
      return;
    }

    let cancelled = false;
    const loadPublicUpcoming = async () => {
      try {
        const regs = await getPublicUpcomingRegistrations(profile.personId as number);
        if (!cancelled) {
          setProfile((prev) => (prev ? { ...prev, upcomingRegistrations: regs } : prev));
          setPublicUpcomingLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          setProfile((prev) => (prev ? { ...prev, upcomingRegistrations: [] } : prev));
          setPublicUpcomingLoaded(true);
        }
      }
    };

    loadPublicUpcoming();
    return () => {
      cancelled = true;
    };
  }, [authLoading, authUserId, canViewPrivateDetails, isOwnProfile, profile, publicUpcomingLoaded]);

  useEffect(() => {
    if (!profile || authLoading || !authUserId || !canViewPrivateDetails || privateProfileLoaded) {
      return;
    }

    let cancelled = false;
    const loadPrivate = async () => {
      try {
        const targetId = isOwnProfile ? authUserId : profile.userDocId;
        if (!targetId) {
          return;
        }

        const data = await getRunnerProfile(targetId, {
          identityMode: isOwnProfile ? 'auth-account' : 'user-document',
          includeParticipations: false,
          includeUpcomingRegistrations: true,
          includePrivateDetails: true
        });
        if (cancelled) {
          return;
        }
        setProfile(data);
        setPrivateProfileLoaded(true);
        
        // Ensure language preference has a default value
        if (isOwnProfile && authUserId) {
          try {
            await ensureLanguagePreference(authUserId);
          } catch (err) {
            console.warn(debugTag, 'Failed to ensure language preference', err);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(debugTag, 'Failed to load private profile details', err);
        }
      }
    };

    loadPrivate();
    return () => {
      cancelled = true;
    };
  }, [authLoading, authUserId, canViewPrivateDetails, debugTag, isOwnProfile, privateProfileLoaded, profile]);

  const hasMoAppearances = useMemo(
    () => moResults.some((e) => isCountedMoClass(e.class)),
    [moResults]
  );

  useEffect(() => {
  }, [authLoading, authUserId, authEmail, profile, isAdmin, personIdMatches, authPersonId, isOwnProfile, canEdit]);

  useEffect(() => {
  }, [detailsExpanded]);

  const getRegistrationStatusLabel = useCallback((status: string | null | undefined): string => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    if (normalized === 'cancelled') {
      return t('registrationStatus.cancelled', {
        defaultValue: registrationStatusLabels[normalized] || normalized,
      });
    }

    return (
      registrationStatusLabels[normalized] ||
      t(`registrationStatus.${normalized}`, { defaultValue: normalized })
    );
  }, [registrationStatusLabels, t]);

  // Helper function to get localized country name (hybrid i18n strategy)
  const getCountryDisplayName = (country: CountryOption): string => {
    if (!country) return '';
    if (country.sortOrder < 500) {
      // Use translation for common/active countries
      return t(`countries.${country.code}`, country.name);
    }
    // Use Firestore verboseName for other countries
    return country.name;
  };

  // Helper function to get localized phone code display
  const getPhoneCodeDisplay = (phoneCode: NationalityCode): string => {
    const countryCode = phoneCode.code;
    const countryName = phoneCode.verboseName;
    
    // Use hybrid i18n strategy for phone codes too
    let localizedName = countryName;
    if (phoneCode.sortOrder && phoneCode.sortOrder < 500) {
      // Try to find matching country for translation by country name
      const matchingCountry = countryOptions.find(c => {
        // Match by country name (case-insensitive)
        return c.name.toLowerCase() === countryName.toLowerCase();
      });
      if (matchingCountry) {
        localizedName = t(`countries.${matchingCountry.code}`, countryName);
      }
    }
    
    return `${localizedName} (+${countryCode.replace('+', '')})`;
  };

  // Create a compatible phone code structure for the UI
  const getCompatiblePhoneCodes = () => {
    if (!phoneCodes || phoneCodes.length === 0) {
      return [];
    }
    
    const compatibleCodes = phoneCodes.map(phoneCode => {
      const localizedName = getPhoneCodeDisplay(phoneCode);
      
      return {
        code: phoneCode.code,
        country: phoneCode.verboseName,
        localizedName: localizedName,
        isCommon: (phoneCode.sortOrder ?? 999) <= 4,
        sortOrder: phoneCode.sortOrder ?? 999
      };
    });
    
    // Sort alphabetically by localized name within groups
    return compatibleCodes.sort((a, b) => {
      // For common countries, preserve sortOrder sequence
      if (a.isCommon && b.isCommon) {
        return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
      }
      // Common countries first, then others
      if (a.isCommon && !b.isCommon) return -1;
      if (!a.isCommon && b.isCommon) return 1;
      
      // Alphabetical sort only for "other countries"
      return a.localizedName.localeCompare(b.localizedName, t('currentLocale', 'en'));
    });
  };

  // Compute compatible phone codes once (like registration form)
  const compatiblePhoneCodes = useMemo(() => {
    return getCompatiblePhoneCodes();
  }, [getCompatiblePhoneCodes]);

  // Find selected phone code (like registration form)
  const selectedPhoneCode = compatiblePhoneCodes.find(pc => 
    pc.code === editableDetails?.phoneCountryCode || 
    pc.code === (editableDetails?.phoneCountryCode || '').replace('+', '')
  ) || null;

  const hasDetailsChanged = useMemo(() => {
    if (!profile || !editableDetails) {
      return false;
    }
    const sanitizedProfileCode = (profile.phoneCountryCode ?? '').replace(/^\+/, '').replace(/\D/g, '');
    const sanitizedProfilePhone = (profile.phone ?? '').replace(/\D/g, '');
    
    // Helper function to compare dates properly
    const datesEqual = (date1: Date | null | undefined, date2: Date | null | undefined) => {
      if (!date1 && !date2) return true;
      if (!date1 || !date2) return false;
      
      try {
        let d1, d2;
        
        // Handle Firestore Timestamp objects and regular dates
        if (date1 && typeof (date1 as any).toDate === 'function') {
          d1 = (date1 as any).toDate();
        } else if (date1 instanceof Date) {
          d1 = date1;
        } else {
          d1 = new Date(date1);
        }
        
        if (date2 && typeof (date2 as any).toDate === 'function') {
          d2 = (date2 as any).toDate();
        } else if (date2 instanceof Date) {
          d2 = date2;
        } else {
          d2 = new Date(date2);
        }
        
        // Check if both dates are valid
        if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
          return false;
        }
        
        return d1.getTime() === d2.getTime();
      } catch {
        return false;
      }
    };
    
    return (
      editableDetails.firstName !== (profile.firstName ?? '') ||
      editableDetails.lastName !== (profile.lastName ?? '') ||
      (editableDetails.displayName ?? '') !== (profile.displayName ?? '') ||
      (editableDetails.phoneCountryCode ?? '') !== sanitizedProfileCode ||
      (editableDetails.phone ?? '') !== sanitizedProfilePhone ||
      !datesEqual(editableDetails.dateOfBirth, profile.dateOfBirth) ||
      editableDetails.yearOfBirth !== (profile.yearOfBirth ?? null) ||
      (editableDetails.nfifLicenseNumber ?? null) !== (profile.nfifLicenseNumber ?? null) ||
      (editableDetails.gender ?? '') !== (profile.gender?.toLowerCase() === 'male' ? 'male' : 
                profile.gender?.toLowerCase() === 'female' ? 'female' : '') ||
      (editableDetails.nationality ?? '') !== (profile.nationality ?? '') ||
      JSON.stringify(editableDetails.representing ?? []) !== JSON.stringify(profile.representing ?? []) ||
      editableDetails.sendRunningOffers !== (profile.sendRunningOffers ?? false) ||
      editableDetails.notifyFutureEvents !== (profile.notifyFutureEvents ?? false) ||
      editableDetails.languagePreference !== (profile.languagePreference ?? null)
    );
  }, [profile, editableDetails]);

  const handleDetailChange = (field: keyof RunnerProfileEditableDetails) => (
    event: any
  ) => {
    let value = event.target.value;
    
    // Handle checkbox events
    if (event.target.type === 'checkbox') {
      value = event.target.checked;
    }
    
    // Handle select events
    if (event.target.value !== undefined && typeof event.target.value === 'string') {
      // For gender select, keep as string
      if (field === 'gender') {
        value = event.target.value;
      }
    }
    
    // Special handling for representing field (array)
    if (field === 'representing' && Array.isArray(value)) {
      // Keep the array as-is for representing field
      // value is already an array from the onChange handler
    } else if (field === 'phoneCountryCode') {
      value = value.replace(/\D/g, '').slice(0, 4);
    } else if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 15);
    } else if (field === 'yearOfBirth') {
      const digits = String(value ?? '').replace(/\D/g, '').slice(0, 4);
      value = digits ? Number(digits) : null;
    } else if (field === 'nfifLicenseNumber') {
      value = String(value ?? '').trim();
    }

    setEditableDetails((prev) => (prev ? { ...prev, [field]: value } : prev));
    setDetailsSuccess(false);
    setDetailsResult(null);
    setDetailsError(null);
  };

  const handleAddClub = () => {
    const trimmedClub = newClub.trim();
    if (trimmedClub && editableDetails) {
      const currentClubs = editableDetails.representing || [];
      if (!currentClubs.includes(trimmedClub)) {
        const updatedClubs = [...currentClubs, trimmedClub];
        handleDetailChange('representing')({ target: { value: updatedClubs } });
      }
      setNewClub('');
      // Return focus to input field for quick entry of next club
      setTimeout(() => {
        clubInputRef.current?.focus();
      }, 0);
    }
  };

  const handleRemoveClub = (clubToRemove: string) => {
    if (editableDetails) {
      const currentClubs = editableDetails.representing || [];
      const updatedClubs = currentClubs.filter(club => club !== clubToRemove);
      handleDetailChange('representing')({ target: { value: updatedClubs } });
    }
  };

  const handleCancelDetails = () => {
    if (!profile) {
      return;
    }
    const sanitizedCode = (profile.phoneCountryCode ?? '').replace(/^\+/, '').replace(/\D/g, '');
    const sanitizedPhone = (profile.phone ?? '').replace(/\D/g, '');

    setEditableDetails({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      displayName: profile.displayName ?? '',
      phoneCountryCode: sanitizedCode,
      phone: sanitizedPhone,
      dateOfBirth: profile.dateOfBirth ? 
                      (() => {
                        try {
                          let date;
                          // Handle Firestore Timestamp objects (check for toDate method before Date check)
                          if (profile.dateOfBirth && typeof (profile.dateOfBirth as any).toDate === 'function') {
                            date = (profile.dateOfBirth as any).toDate();
                          } else if (profile.dateOfBirth instanceof Date) {
                            date = profile.dateOfBirth;
                          } else {
                            date = new Date(profile.dateOfBirth);
                          }
                          return date instanceof Date && !isNaN(date.getTime()) ? date : null;
                        } catch {
                          return null;
                        }
                      })() : null,
      yearOfBirth: profile.yearOfBirth ?? null,
      nfifLicenseNumber: profile.nfifLicenseNumber ?? null,
      gender: (profile.gender?.toLowerCase() === 'male' ? 'male' : 
                profile.gender?.toLowerCase() === 'female' ? 'female' : '') as '' | 'male' | 'female',
      nationality: profile.nationality ?? '',
      representing: profile.representing ?? [],
      sendRunningOffers: profile.sendRunningOffers ?? false,
      notifyFutureEvents: profile.notifyFutureEvents ?? false,
      languagePreference: profile.languagePreference ?? null
    });
    setDetailsSuccess(false);
    setDetailsError(null);
  };

  const handleSaveDetails = async () => {
    if (!profile || !editableDetails || !hasDetailsChanged) {
      console.log(debugTag, 'Save skipped', {
        hasProfile: Boolean(profile),
        hasEditableDetails: Boolean(editableDetails),
        hasDetailsChanged
      });
      return;
    }

    setDetailsSaving(true);
    setDetailsError(null);

    if (editableDetails.nfifLicenseNumber) {
      const license = String(editableDetails.nfifLicenseNumber).trim();
      const currentYear = new Date().getFullYear();
      const match = license.match(/^(\d{6})-(\d{4})$/);
      const year = match ? Number(match[2]) : NaN;
      const isValid = Boolean(match) && (year === currentYear || year === currentYear + 1);

      if (!isValid) {
        setDetailsError(
          t('runners.profile.personalDetails.nfifLicenseNumberInvalid', {
            currentYear,
            nextYear: currentYear + 1
          })
        );
        setDetailsSaving(false);
        return;
      }
    }

    if (editableDetails.dateOfBirth && editableDetails.yearOfBirth) {
      const dateYear = editableDetails.dateOfBirth.getFullYear();
      if (dateYear !== editableDetails.yearOfBirth) {
        setDetailsError(t('runners.profile.personalDetails.dateYearMismatch', {
          dateYear: dateYear,
          yearOfBirth: editableDetails.yearOfBirth
        }));
        setDetailsSaving(false);
        return;
      }
    }

    try {
      // Auto-add any remaining club text before saving
      if (newClub.trim() && editableDetails) {
        const trimmedClub = newClub.trim();
        const currentClubs = editableDetails.representing || [];
        if (!currentClubs.includes(trimmedClub)) {
          const updatedClubs = [...currentClubs, trimmedClub];
          setEditableDetails(prev => prev ? { ...prev, representing: updatedClubs } : prev);
        }
        setNewClub('');
      }

      const sanitizedCode = (editableDetails.phoneCountryCode ?? '').replace(/\D/g, '');
      const sanitizedPhone = (editableDetails.phone ?? '').replace(/\D/g, '');
      // Store phone country code without + sign to match registration format
      const storedCode = sanitizedCode;

      const targetUserId = profile.userDocId;
      if (!targetUserId) {
        throw new Error('No valid user ID found for profile update');
      }
      const updateDetails: Parameters<typeof updateRunnerProfileDetails>[1] = {
        firstName: editableDetails.firstName,
        lastName: editableDetails.lastName,
        displayName: editableDetails.displayName,
        phoneCountryCode: storedCode,
        phone: sanitizedPhone,
        dateOfBirth: editableDetails.dateOfBirth,
        yearOfBirth: editableDetails.yearOfBirth,
        nfifLicenseNumber: editableDetails.nfifLicenseNumber,
        gender: editableDetails.gender,
        nationality: editableDetails.nationality,
        representing: editableDetails.representing,
        sendRunningOffers: editableDetails.sendRunningOffers,
        notifyFutureEvents: editableDetails.notifyFutureEvents,
        languagePreference: editableDetails.languagePreference
      };
      const loadedIdentity = { userDocId: profile.userDocId, personId: profile.personId };
      let updateResult: void | RunnerProfileUpdateResult;
      if (isOwnProfile) {
        updateResult = await updateOwnRunnerProfileDetails(authUserId || targetUserId, updateDetails, loadedIdentity);
      } else {
        updateResult = await updateRunnerProfileDetails(targetUserId, updateDetails, loadedIdentity);
      }

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              firstName: editableDetails.firstName,
              lastName: editableDetails.lastName,
              displayName: editableDetails.displayName,
              phoneCountryCode: storedCode,
              phone: sanitizedPhone,
              dateOfBirth: editableDetails.dateOfBirth,
              yearOfBirth: editableDetails.yearOfBirth,
              nfifLicenseNumber: editableDetails.nfifLicenseNumber,
              gender: editableDetails.gender,
              nationality: editableDetails.nationality,
              representing: editableDetails.representing,
              sendRunningOffers: editableDetails.sendRunningOffers,
              notifyFutureEvents: editableDetails.notifyFutureEvents,
              languagePreference: editableDetails.languagePreference
            }
          : prev
      );
      setDetailsResult(updateResult || null);
      setDetailsSuccess(true);
    } catch (err: any) {
      setDetailsError(err?.message || t('runners.profile.detailsUpdateFailed'));
      console.error(debugTag, 'Failed saving personal details', {
        userId: profile.userDocId,
        error: err
      });
    } finally {
      setDetailsSaving(false);
    }
  };

  const handleClaimProfile = async () => {
    if (!profile || !authUserId) return;
    
    setClaimLoading(true);
    setClaimError(null);
    try {
      const result = await submitClaimProfileRequest({
        targetProfileDocId: profile.userDocId || '',
        targetPersonId: profile.personId ?? null,
        targetName: `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
      });
      
      if (result.success) {
        setClaimSuccess(true);
        setPendingClaimStatus('claim');
      } else {
        setClaimError(result.error || t('runnerProfile.claimFailed'));
      }
    } catch (err: any) {
      console.error(debugTag, 'Failed to submit claim request', err);
      setClaimError(err?.message || t('runnerProfile.claimFailed'));
    } finally {
      setClaimLoading(false);
    }
  };

  // Open unclaim confirmation dialog
  const handleUnclaimClick = () => {
    setUnclaimDialogOpen(true);
  };

  // Actually submit the unclaim request after confirmation
  const handleUnclaimConfirm = async () => {
    if (!profile || !authUserId) return;
    
    setUnclaimDialogOpen(false);
    setClaimLoading(true);
    setClaimError(null);
    try {
      const result = await submitUnclaimProfileRequest({
        profileDocId: profile.userDocId || '',
        personId: profile.personId ?? null,
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
        reason: keepPersonalDetails 
          ? 'User requested unclaim (keep personal details)'
          : 'User requested unclaim (clear personal details)',
        keepPersonalDetails
      });
      
      if (result.success) {
        setClaimSuccess(true);
        setPendingClaimStatus('unclaim');
      } else {
        setClaimError(result.error || t('runnerProfile.unlinkFailed'));
      }
    } catch (err: any) {
      console.error(debugTag, 'Failed to submit unclaim request', err);
      setClaimError(err?.message || t('runnerProfile.unlinkFailed'));
    } finally {
      setClaimLoading(false);
    }
  };

  // Check for pending claim/unclaim requests
  const checkPendingClaimStatus = useCallback(async () => {
    if (!authUserId || !profile) return;
    
    try {
      const db = getFirestore();
      
      // Check for pending claim request
      const claimQuery = query(
        collection(db, 'adminTasks'),
        where('type', '==', 'claim_profile'),
        where('status', '==', 'open'),
        where('requesterUid', '==', authUserId),
        where('targetProfileDocId', '==', profile.userDocId),
        limit(1)
      );
      
      const claimSnapshot = await getDocs(claimQuery);
      if (!claimSnapshot.empty) {
        setPendingClaimStatus('claim');
        return;
      }
      
      // Check for pending unclaim request
      const unclaimQuery = query(
        collection(db, 'adminTasks'),
        where('type', '==', 'unclaim_profile'),
        where('status', '==', 'open'),
        where('requesterUid', '==', authUserId),
        where('targetProfileDocId', '==', profile.userDocId),
        limit(1)
      );
      
      const unclaimSnapshot = await getDocs(unclaimQuery);
      if (!unclaimSnapshot.empty) {
        setPendingClaimStatus('unclaim');
        return;
      }
      
      // No pending requests
      setPendingClaimStatus(null);
    } catch (err) {
      console.error(debugTag, 'Failed to check pending claim status', err);
    }
  }, [authUserId, profile]);

  // Check for pending claims when profile loads
  useEffect(() => {
    if (profile && authUserId) {
      checkPendingClaimStatus();
    }
  }, [profile, authUserId, checkPendingClaimStatus]);

  const renderParticipations = (participations: RunnerParticipation[]) => {
    if (participations.length === 0) {
      return null;
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid',
          borderColor: 'primary.light',
          boxShadow: '0 6px 18px rgba(25, 118, 210, 0.16)',
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderWidth: 2,
          borderRadius: 2
        }}
      >
        <Table
          sx={{
            '& .MuiTableHead-root': {
              backgroundColor: 'rgba(25, 118, 210, 0.18)',
              '& .MuiTableCell-root': {
                color: 'primary.contrastText',
                backgroundColor: 'rgba(25, 118, 210, 0.35)',
                borderColor: 'rgba(25, 118, 210, 0.2)'
              }
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&:nth-of-type(even)': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)'
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.18)'
              }
            },
            '& .MuiTableCell-root': {
              borderColor: 'rgba(25, 118, 210, 0.25)'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('runners.profile.kutc.table.edition')}</TableCell>
              <TableCell>{t('runners.profile.kutc.table.race')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.raceRank')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.raceTime')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.totalRank')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.totalLoops')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.totalTime')}</TableCell>
              <TableCell>{t('runners.profile.kutc.table.status')}</TableCell>
              <TableCell align="right">{t('runners.profile.kutc.table.analysis')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participations.map((participation) => {
              const editionLabel = `KUTC ${participation.year}`;
              const raceTime = formatTimeDisplay(participation.raceTimeDisplay, participation.raceTimeSeconds);
              const totalTime = formatTimeDisplay(participation.totalTimeDisplay, participation.totalTimeSeconds);
              const loops = formatLoops(participation.loopsCompleted);
              const distanceKey = participation.distanceKey || 'total';
              return (
                <TableRow key={participation.editionId}>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon={<ChevronRight fontSize="small" />}
                      onClick={() => navigate(`/kutc/results/${participation.editionId}?distance=total`)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'primary.main',
                        color: 'primary.dark',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          backgroundColor: 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    >
                      {editionLabel}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon={<ChevronRight fontSize="small" />}
                      onClick={() => {
                        const key = distanceKey || 'total';
                        navigate(`/kutc/results/${participation.editionId}?distance=${encodeURIComponent(key)}`);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: 'primary.main',
                        color: 'primary.dark',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          backgroundColor: 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    >
                      {participation.raceName}
                    </Button>
                  </TableCell>
                  <TableCell align="right">{formatRank(participation.raceRank)}</TableCell>
                  <TableCell align="right">{raceTime}</TableCell>
                  <TableCell align="right">{formatRank(participation.totalRank)}</TableCell>
                  <TableCell align="right">{loops}</TableCell>
                  <TableCell align="right">{totalTime}</TableCell>
                  <TableCell>{participation.status || '—'}</TableCell>
                  <TableCell align="right">
                    {participation.hasCheckpointData ? (
                      <Button
                        component={RouterLink}
                        to={`/runners/${profile?.routeId}/kutc/${participation.editionId}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          borderColor: 'primary.main',
                          color: 'primary.dark',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'rgba(25, 118, 210, 0.12)'
                          }
                        }}
                      >
                        {t('common.view')}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('runners.profile.kutc.table.na')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderMoParticipation = (entries: MOResultEntry[]) => {
    if (moLoading) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    const entriesToShow = entries.filter((e) => isCountedMoClass(e.class));
    if (!entriesToShow.length) {
      return null;
    }

    const headerGender = entriesToShow.find((e) => isCompetitionClass(e.class) && e.gender)?.gender;
    const genderHeaderLabel = headerGender === 'Male'
      ? t('runners.profile.gender.men')
      : headerGender === 'Female'
        ? t('runners.profile.gender.women')
        : t('runners.profile.gender.gender');

    return (
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid',
          borderColor: 'success.light',
          boxShadow: '0 6px 18px rgba(46, 125, 50, 0.16)',
          backgroundColor: 'rgba(46, 125, 50, 0.08)',
          borderWidth: 2,
          borderRadius: 2
        }}
      >
        <Table
          sx={{
            '& .MuiTableHead-root': {
              backgroundColor: 'rgba(46, 125, 50, 0.18)',
              '& .MuiTableCell-root': {
                color: 'common.white',
                backgroundColor: 'rgba(46, 125, 50, 0.35)',
                borderColor: 'rgba(46, 125, 50, 0.2)'
              }
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              backgroundColor: 'rgba(46, 125, 50, 0.08)',
              '&:nth-of-type(even)': {
                backgroundColor: 'rgba(46, 125, 50, 0.12)'
              },
              '&:hover': {
                backgroundColor: 'rgba(46, 125, 50, 0.18)'
              }
            },
            '& .MuiTableCell-root': {
              borderColor: 'rgba(46, 125, 50, 0.25)'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('runners.profile.mo.table.edition')}</TableCell>
              <TableCell>{t('runners.profile.mo.table.class')}</TableCell>
              <TableCell align="right">{t('runners.profile.mo.table.rankGender', { gender: genderHeaderLabel })}</TableCell>
              <TableCell align="right">{t('runners.profile.mo.table.time')}</TableCell>
              <TableCell align="right">
                <Tooltip
                  title={t('runners.profile.mo.table.aggTooltip')}
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  <Button variant="text" size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, color: 'text.primary' }}>
                    {t('runners.profile.mo.table.rankAgg')}
                  </Button>
                </Tooltip>
              </TableCell>
              <TableCell align="right">{t('runners.profile.mo.table.adjusted')}</TableCell>
              <TableCell>{t('runners.profile.mo.table.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entriesToShow
              .slice()
              .sort((a, b) => {
                const ya = a.editionYear ?? 0;
                const yb = b.editionYear ?? 0;
                return yb - ya;
              })
              .map((entry) => {
                const editionLabel = entry.editionYear ? `MO ${entry.editionYear}` : entry.editionId;
                const time = formatTimeDisplay(entry.timeDisplay ?? null, entry.timeSeconds ?? null);
                const adjusted = formatTimeDisplay(entry.adjustedDisplay ?? null, entry.adjustedSeconds ?? null);
                const isComp = isCompetitionClass(entry.class);
                const genderKey = (entry.gender ?? '').toString();
                const cache = entry.editionId ? moRankCache[entry.editionId] : undefined;
                const nameKey = normalizeNameKey(entry.fullName ?? '') as string | null;
                const rowGenderRank = isComp && genderKey
                  ? (
                      (nameKey && cache?.byGenderName?.[genderKey]?.[nameKey]) ||
                      (entry.rankTime ?? null)
                    )
                  : null;
                const rowAggRank = isComp
                  ? (
                      (nameKey && cache?.adjustedByName?.[nameKey]) ||
                      (entry.rankAdjusted ?? null)
                    )
                  : null;
                return (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        endIcon={<ChevronRight fontSize="small" />}
                        onClick={() => navigate(`/mo/results/${entry.editionId}`)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: 'success.dark',
                          color: 'common.white',
                          backgroundColor: 'rgba(46, 125, 50, 0.18)',
                          '&:hover': {
                            borderColor: 'success.dark',
                            backgroundColor: 'rgba(46, 125, 50, 0.28)'
                          }
                        }}
                      >
                        {editionLabel}
                      </Button>
                    </TableCell>
                    <TableCell>{formatMoClass(entry.class, t)}</TableCell>
                    <TableCell align="right">{formatRank(rowGenderRank as any)}</TableCell>
                    <TableCell align="right">{time}</TableCell>
                    <TableCell align="right">{formatRank(rowAggRank as any)}</TableCell>
                    <TableCell align="right">{adjusted}</TableCell>
                    <TableCell>{formatMoStatus(entry.status, t)}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handlePayNow = async (registrationId: string) => {
    setPaymentError(null);
    setPayingRegistrationId(registrationId);
    try {
      await redirectToStripeCheckout(registrationId, t('common.languageCode', { defaultValue: 'en' }));
    } catch (err: any) {
      console.error('[RunnerProfilePage] Payment error', err);
      setPaymentError(err?.message || t('runners.profile.upcoming.paymentError'));
      setPayingRegistrationId(null);
    }
  };

  const handleGetReceipt = (event: React.MouseEvent<HTMLElement>, registration: RunnerUpcomingRegistration) => {
    // Include both payments and refunds (negative amounts)
    const paymentsAndRefunds = (registration.payments || [])
      .filter((p: any) => p.method === 'stripe' || p.method === 'stripe_refund')
      .filter((p: any) => p.receiptUrl || p.chargeId)
      .map((p: any) => ({
        ...p,
        isRefund: p.amount < 0 || p.method === 'stripe_refund'
      }));
    
    if (paymentsAndRefunds.length === 0) {
      alert(t('runners.profile.upcoming.receiptNotAvailable'));
      return;
    }
    
    // Always show menu - even for single payment, so user can see date/amount before clicking
    setReceiptMenuPayments(paymentsAndRefunds);
    setReceiptMenuAnchor(event.currentTarget);
  };
  
  const openReceiptUrl = (payment: any) => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    } else if (payment.chargeId) {
      const isTest = payment.chargeId.startsWith('ch_test_');
      const baseUrl = isTest ? EXTERNAL_URLS.stripe.testDashboard : EXTERNAL_URLS.stripe.dashboard;
      const dashboardUrl = `${baseUrl}/payments/${payment.chargeId}`;
      window.open(dashboardUrl, '_blank');
    }
  };
  
  const handleReceiptMenuClose = () => {
    setReceiptMenuAnchor(null);
    setReceiptMenuPayments([]);
  };

  const openCancellationDialog = (registration: RunnerUpcomingRegistration) => {
    const preferredCode =
      cancellationReasonOptions.find(
        (option) => String(option.code || '').trim().toLowerCase() === DEFAULT_CANCELLATION_REASON_CODE.toLowerCase()
      )?.code || cancellationReasonOptions[0]?.code || DEFAULT_CANCELLATION_REASON_CODE;

    setSelectedCancellationRegistration(registration);
    setSelectedCancellationReasonCode(String(preferredCode));
    setSelectedCancellationReasonText('');
    setCancellationAcknowledged(false);
    setCancellationError(null);
    setCancellationDialogOpen(true);
  };

  const handleCancellationContinue = () => {
    setCancellationDialogOpen(false);
    setCancellationFinalDialogOpen(true);
  };

  const handleCancellationBack = () => {
    setCancellationFinalDialogOpen(false);
    setCancellationDialogOpen(true);
  };

  const resetCancellationDialogs = () => {
    setCancellationDialogOpen(false);
    setCancellationFinalDialogOpen(false);
    setSelectedCancellationRegistration(null);
    setSelectedCancellationReasonCode(DEFAULT_CANCELLATION_REASON_CODE);
    setSelectedCancellationReasonText('');
    setCancellationAcknowledged(false);
  };

  const handleConfirmCancellation = async () => {
    const registration = selectedCancellationRegistration;
    if (!registration?.registrationId) {
      return;
    }

    setCancellationError(null);
    setCancellationSuccess(null);
    setCancellingRegistrationId(registration.registrationId);

    try {
      await cancelRegistrationByUser({
        registrationId: registration.registrationId,
        reasonCode: selectedCancellationReasonCode || DEFAULT_CANCELLATION_REASON_CODE,
        reasonText: selectedCancellationReasonText.trim() || undefined,
      });

      setProfile((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          upcomingRegistrations: (prev.upcomingRegistrations || []).filter(
            (item) => item.registrationId !== registration.registrationId
          ),
        };
      });

      setCancellationSuccess(t('runners.profile.upcoming.cancel.success'));
      resetCancellationDialogs();
    } catch (err: any) {
      console.error('[RunnerProfilePage] Cancellation failed', err);
      setCancellationError(err?.message || t('runners.profile.upcoming.cancel.failed'));
    } finally {
      setCancellingRegistrationId(null);
    }
  };
  
  const formatPaymentDate = (payment: any): string => {
    if (!payment.date) return '';
    const date = payment.date?.toDate ? payment.date.toDate() : new Date(payment.date);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const renderUpcomingRegistrations = (registrations: RunnerUpcomingRegistration[]) => {
    if (!registrations.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('runners.profile.upcoming.none')}
        </Typography>
      );
    }

    const stripeEnabled = isStripePaymentsEnabled();

    return (
      <Box>
        {paymentError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPaymentError(null)}>
            {paymentError}
          </Alert>
        )}
        {cancellationError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCancellationError(null)}>
            {cancellationError}
          </Alert>
        )}
        {cancellationSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCancellationSuccess(null)}>
            {cancellationSuccess}
          </Alert>
        )}
        {registrations.map((registration, index) => {
          const isLast = index === registrations.length - 1;
          const typeLabel = registration.registrationType === 'kutc'
            ? 'KUTC'
            : registration.registrationType === 'mo'
              ? 'MO'
              : t('editions.event');
          const registrationStatus = String(registration.status || '').toLowerCase();
          const statusLabel = getRegistrationStatusLabel(registration.status);
          const editionYear = parseYearFromEditionId(registration.editionId);
          const hasYearInName = editionYear
            ? String(registration.eventName || '').includes(String(editionYear))
            : false;
          const displayName = editionYear && !hasYearInName
            ? `${registration.eventName} (${editionYear})`
            : registration.eventName;
          const distanceLabel = registration.raceDistanceLabel || registration.raceDistance || null;
          const updatedAtLabel = registration.updatedAt ? formatDateTimeDisplay(registration.updatedAt, t) : null;

          const paymentRequired = registration.paymentRequired ?? 0;
          const paymentMade = registration.paymentMade ?? 0;
          const paymentRemaining = Math.max(0, paymentRequired - paymentMade);
          const showPayButton = stripeEnabled && isOwnProfile && paymentRemaining > 0;
          const showReceiptButton = registration.paymentStatus === 'completed' && registration.payments && registration.payments.length > 0;
          const isPaying = payingRegistrationId === registration.registrationId;
          const cancellationUnavailableReason = getCancellationUnavailableReason(registration, t);
          const canCancelRegistration = isOwnProfile && !cancellationUnavailableReason;
          const isCancellingThisRegistration = cancellingRegistrationId === registration.registrationId;

          return (
            <Box
              key={registration.registrationId}
              sx={{
                py: 1.5,
                borderBottom: !isLast ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
                {/* Icon */}
                <Box sx={{ minWidth: 40, display: 'flex', alignItems: 'center' }}>
                  <CalendarCheck size={20} />
                </Box>
                
                {/* Main content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Primary info */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {displayName}
                    </Typography>
                    <Chip size="small" label={typeLabel} color={typeLabel === 'MO' ? 'success' : 'primary'} variant="outlined" />
                    {distanceLabel && (
                      <Chip size="small" label={distanceLabel} variant="outlined" />
                    )}
                    {statusLabel && (
                      <Chip size="small" label={statusLabel} color={registrationStatus === 'confirmed' ? 'success' : 'warning'} variant="outlined" />
                    )}
                  </Stack>
                  
                  {/* Secondary info */}
                  <Stack direction="column" spacing={0.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('runners.profile.upcoming.starts', { date: formatDateTimeDisplay(registration.startTime, t) })}
                      </Typography>
                      {typeof registration.registrationNumber === 'number' && (
                        <Typography variant="body2" color="text.secondary">
                          {t('runners.profile.upcoming.registrationNumber', { number: registration.registrationNumber })}
                        </Typography>
                      )}
                      {updatedAtLabel && (
                        <Typography variant="body2" color="text.secondary">
                          {t('runners.profile.upcoming.lastUpdated', { date: updatedAtLabel })}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('runners.profile.upcoming.paymentRequired', { amount: paymentRequired })}
                      </Typography>
                      <Typography variant="body2" color={paymentRemaining > 0 ? 'warning.main' : 'success.main'}>
                        {t('runners.profile.upcoming.paymentMade', { amount: paymentMade })}
                      </Typography>
                      {paymentRemaining > 0 && (
                        <Typography variant="body2" color="warning.main">
                          {t('runners.profile.upcoming.paymentRemaining', { amount: paymentRemaining })}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
                
                {/* Action buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  minWidth: { xs: '100%', sm: 'auto' },
                  flexShrink: 0
                }}>
                  {showPayButton && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      disabled={isPaying}
                      onClick={() => handlePayNow(registration.registrationId)}
                      sx={{ textTransform: 'none', minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      {isPaying ? <CircularProgress size={16} color="inherit" /> : t('runners.profile.upcoming.payNow')}
                    </Button>
                  )}
                  {showReceiptButton && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => handleGetReceipt(e, registration)}
                        sx={{ textTransform: 'none', minWidth: { xs: '100%', sm: 'auto' } }}
                        startIcon={<Info size={16} />}
                        endIcon={(registration.payments?.filter((p: any) => p.receiptUrl || p.chargeId).length || 0) >= 1 ? <ChevronDown size={14} /> : undefined}
                      >
                        {t('runners.profile.upcoming.getReceipt')}
                      </Button>
                      <Menu
                        anchorEl={receiptMenuAnchor}
                        open={Boolean(receiptMenuAnchor)}
                        onClose={handleReceiptMenuClose}
                      >
                        {receiptMenuPayments.map((payment, idx) => (
                          <MenuItem
                            key={idx}
                            onClick={() => {
                              openReceiptUrl(payment);
                              handleReceiptMenuClose();
                            }}
                            sx={{
                              color: payment.isRefund ? 'error.main' : 'inherit'
                            }}
                          >
                            {payment.isRefund && '↩ '}
                            {formatPaymentDate(payment)} - {Math.abs(payment.amount)} NOK
                            {payment.isRefund && ' (Refund)'}
                            {payment.paymentMethodDetails?.card && ` (${payment.paymentMethodDetails.card.brand} •••• ${payment.paymentMethodDetails.card.last4})`}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                  {isOwnProfile && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        await setEvent(registration.editionId);
                        navigate('/register');
                      }}
                      sx={{ textTransform: 'none', minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      {t('runners.profile.upcoming.reviewRegistration')}
                    </Button>
                  )}
                  {isOwnProfile && (canCancelRegistration || cancellationUnavailableReason) && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={!canCancelRegistration || isCancellingThisRegistration}
                      onClick={() => openCancellationDialog(registration)}
                      sx={{ textTransform: 'none', minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      {isCancellingThisRegistration ? <CircularProgress size={16} color="inherit" /> : t('runners.profile.upcoming.cancel.action')}
                    </Button>
                  )}
                </Box>
              </Stack>
              {isOwnProfile && cancellationUnavailableReason && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {cancellationUnavailableReason}.{' '}
                  {t('runners.profile.upcoming.cancel.servicePortalPrefix')}{' '}
                  <Link href={SERVICE_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                    {t('runners.profile.upcoming.cancel.servicePortalLink')}
                  </Link>.
                </Alert>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!profile || !stats) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="info">{t('runners.profile.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header with name, subtitle, and claim buttons */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'flex-start' },
        gap: 2
      }}>
        <Box>
          <Typography variant="h3" gutterBottom>
            {stats.name}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: isOwnProfile ? 'primary.main' : 'text.secondary',
              fontWeight: isOwnProfile ? 600 : 400,
              ...(isOwnProfile && {
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                display: 'inline-block'
              })
            }}
          >
            {isOwnProfile ? t('runners.profile.myPageSubtitle') : t('runners.profile.pageSubtitle')}
          </Typography>
        </Box>

        {/* Claim/Unclaim Profile Buttons - flush right */}
        {!authLoading && authUserId && !claimSuccess && !pendingClaimStatus && (
          <Box sx={{ flexShrink: 0 }}>
            {!isOwnProfile && !isAdmin && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClaimProfile}
                disabled={claimLoading}
                startIcon={claimLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ textTransform: 'none' }}
              >
                {t('runnerProfile.thisIsMe')}
              </Button>
            )}
            {isOwnProfile && (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleUnclaimClick}
                disabled={claimLoading}
                startIcon={claimLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ textTransform: 'none' }}
              >
                {t('runnerProfile.thisIsNotMe')}
              </Button>
            )}
          </Box>
        )}

        {/* Pending Claim Status */}
        {!authLoading && authUserId && pendingClaimStatus && (
          <Box sx={{ flexShrink: 0 }}>
            <Alert 
              severity="info" 
              sx={{ 
                py: 1,
                px: 2,
                '& .MuiAlert-message': {
                  py: 0.5
                }
              }}
            >
              {pendingClaimStatus === 'claim' 
                ? t('runnerProfile.claimPending') 
                : t('runnerProfile.unclaimPending')
              }
            </Alert>
          </Box>
        )}
      </Box>

      {/* Claim Error */}
      {claimError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {claimError}
        </Alert>
      )}

      {/* Claim Success Message */}
      {claimSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {isOwnProfile ? t('runnerProfile.unlinkSubmitted') : t('runnerProfile.claimSubmitted')}
        </Alert>
      )}

      {!authLoading && !isOwnProfile && !isAdmin && (
        <Alert severity="info" sx={{ mb: 4 }}>
          {t('runners.profile.publicProfileNotice')}
        </Alert>
      )}

      {canViewPrivateDetails && (
        <Paper sx={{ mb: 4 }}>
          <Button
            fullWidth
            onClick={() => setDetailsExpanded((prev) => !prev)}
            sx={{
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              textTransform: 'none',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
            endIcon={<ChevronDown size={20} style={{ transform: detailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
          >
            <Typography variant="h5" component="span">
              {t('runners.profile.personalDetails.title')}
            </Typography>
            <Typography variant="body2" component="span" color="text.secondary">
              {detailsExpanded ? t('runners.profile.personalDetails.hide') : t('runners.profile.personalDetails.show')}
            </Typography>
          </Button>
          <Collapse in={detailsExpanded} timeout="auto" unmountOnExit>
            <Divider />
            <Box sx={{ px: 3, py: 3 }}>
              {detailsSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {detailsResult?.publicPublication === 'held'
                    ? t('runners.profile.personalDetails.savedPublicHeld', { defaultValue: 'Your profile was saved, but its public profile was not updated. Identity review is required.' })
                    : detailsResult?.publicPublication === 'unassigned'
                      ? t('runners.profile.personalDetails.savedPublicUnassigned', { defaultValue: 'Your profile was saved, but no public runner profile is assigned yet.' })
                      : t('runners.profile.personalDetails.saved')}
                </Alert>
              )}
              {detailsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {detailsError}
                </Alert>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.firstName')}
                    fullWidth
                    value={editableDetails?.firstName ?? ''}
                    onChange={handleDetailChange('firstName')}
                    disabled={detailsSaving || !canEdit}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.lastName')}
                    fullWidth
                    value={editableDetails?.lastName ?? ''}
                    onChange={handleDetailChange('lastName')}
                    disabled={detailsSaving || !canEdit}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    id="phone-code-autocomplete"
                    options={compatiblePhoneCodes}
                    getOptionLabel={(option) => option ? option.localizedName || `${option.country} (+${option.code.replace('+', '')})` : ''}
                    groupBy={(option) => option?.isCommon ? t('form.commonCountries') : t('form.allCountries')}
                    value={selectedPhoneCode}
                    onChange={(_, newValue) => {
                      const code = newValue ? newValue.code : '';
                      setEditableDetails((prev) => (prev ? { ...prev, phoneCountryCode: code } : null));
                    }}
                    loading={isLoadingPhoneCodes}
                    disabled={detailsSaving || !canEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('runners.profile.personalDetails.phoneCountryCode')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingPhoneCodes ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        inputProps={{
                          ...params.inputProps,
                          pattern: '[0-9]*',
                          maxLength: 4,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.phoneNumber')}
                    fullWidth
                    value={editableDetails?.phone ?? ''}
                    onChange={handleDetailChange('phone')}
                    disabled={detailsSaving || !canEdit}
                    placeholder={t('runners.profile.personalDetails.phonePlaceholder')}
                    InputProps={{ inputMode: 'numeric' }}
                    inputProps={{ pattern: '[0-9]*', maxLength: 15 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.displayName')}
                    fullWidth
                    value={editableDetails?.displayName ?? ''}
                    onChange={handleDetailChange('displayName')}
                    disabled={detailsSaving || !canEdit}
                    helperText={t('runners.profile.personalDetails.displayNameHelper')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.dateOfBirth')}
                    type="date"
                    fullWidth
                    value={editableDetails?.dateOfBirth ? 
                      (() => {
                        try {
                          const date = typeof editableDetails.dateOfBirth === 'string' ? 
                            new Date(editableDetails.dateOfBirth) : 
                            editableDetails.dateOfBirth;
                          return date instanceof Date && !isNaN(date.getTime()) ? 
                            date.toISOString().split('T')[0] : '';
                        } catch {
                          return '';
                        }
                      })() : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      handleDetailChange('dateOfBirth')({ target: { value: date } });
                    }}
                    disabled={detailsSaving || !canEdit}
                    helperText={t('runners.profile.personalDetails.dateOfBirthHelper')}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.yearOfBirth')}
                    type="number"
                    fullWidth
                    value={editableDetails?.yearOfBirth ?? ''}
                    onChange={handleDetailChange('yearOfBirth')}
                    disabled={detailsSaving || !canEdit}
                    helperText={t('runners.profile.personalDetails.yearOfBirthHelper')}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear(),
                      placeholder: 'YYYY'
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.nfifLicenseNumber')}
                    fullWidth
                    value={editableDetails?.nfifLicenseNumber ?? ''}
                    onChange={handleDetailChange('nfifLicenseNumber')}
                    disabled={detailsSaving || !canEdit}
                    helperText={t('runners.profile.personalDetails.nfifLicenseNumberHelper', {
                      currentYear: new Date().getFullYear(),
                      nextYear: new Date().getFullYear() + 1
                    })}
                    placeholder={`123456-${new Date().getFullYear()}`}
                    inputProps={{ pattern: '\\d{6}-\\d{4}' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={detailsSaving || !canEdit}>
                    <InputLabel>{t('runners.profile.personalDetails.gender')}</InputLabel>
                    <Select
                      value={editableDetails?.gender ?? ''}
                      onChange={handleDetailChange('gender')}
                      label={t('runners.profile.personalDetails.gender')}
                    >
                      <MenuItem value="">{t('runners.profile.personalDetails.genderEmpty')}</MenuItem>
                      <MenuItem value="male">{t('runners.profile.personalDetails.genderMale')}</MenuItem>
                      <MenuItem value="female">{t('runners.profile.personalDetails.genderFemale')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={countryOptions}
                    getOptionLabel={(option) => getCountryDisplayName(option)}
                    groupBy={(option) => option.isCommon ? t('form.commonCountries') : t('form.allCountries')}
                    value={countryOptions.find(country => country.code === editableDetails?.nationality) || null}
                    onChange={(_, newValue) => {
                      const code = newValue ? newValue.code : '';
                      setEditableDetails((prev) => (prev ? { ...prev, nationality: code } : null));
                    }}
                    loading={isLoadingCountries}
                    disabled={detailsSaving || !canEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('runners.profile.personalDetails.nationality')}
                        helperText={t('runners.profile.personalDetails.nationalityHelper')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('runners.profile.personalDetails.clubs')}
                  </Typography>
                  
                  {/* List of clubs - moved to top */}
                  {(editableDetails?.representing ?? []).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {(editableDetails?.representing ?? []).map((club, index) => (
                        <Chip
                          key={index}
                          label={club}
                          onDelete={() => handleRemoveClub(club)}
                          disabled={detailsSaving || !canEdit}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                  
                  {/* Add new club input */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      inputRef={clubInputRef}
                      value={newClub}
                      onChange={(e) => setNewClub(e.target.value)}
                      placeholder={t('runners.profile.personalDetails.clubsPlaceholder')}
                      disabled={detailsSaving || !canEdit}
                      helperText={t('runners.profile.personalDetails.clubsHelper')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddClub();
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddClub}
                      disabled={detailsSaving || !canEdit || !newClub.trim()}
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        mt: 1.5, // Reduced from 2.5 to move button higher
                        minWidth: '40px', // Ensure consistent button size
                        height: '40px', // Match typical contained button height
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: 'black',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                        '&:disabled': {
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'black',
                          opacity: 0.6
                        },
                        '& .MuiButton-root': {
                          minHeight: '40px', // Ensure minimum height
                        }
                      }}
                    >
                      <Plus />
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editableDetails?.sendRunningOffers ?? false}
                        onChange={handleDetailChange('sendRunningOffers')}
                        disabled={detailsSaving || !canEdit}
                      />
                    }
                    label={t('runners.profile.personalDetails.sendRunningOffers')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editableDetails?.notifyFutureEvents ?? false}
                        onChange={handleDetailChange('notifyFutureEvents')}
                        disabled={detailsSaving || !canEdit}
                      />
                    }
                    label={t('runners.profile.personalDetails.notifyFutureEvents')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {t('form.languagePreferenceLabel')}
                        </Typography>
                        <Tooltip title={t('form.languagePreferenceHelper')}>
                          <Info fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      row
                      value={editableDetails?.languagePreference || ''}
                      onChange={handleDetailChange('languagePreference')}
                    >
                      <FormControlLabel value="no" control={<Radio disabled={detailsSaving || !canEdit} />} label={t('form.norwegian')} />
                      <FormControlLabel value="en" control={<Radio disabled={detailsSaving || !canEdit} />} label={t('form.english')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('runners.profile.personalDetails.email')}
                    fullWidth
                    value={profile.email}
                    disabled
                    helperText={t('runners.profile.personalDetails.emailHelper')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('runners.profile.personalDetails.emailTooltip')}>
                            <Info size={16} />
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              {canEdit && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                  <Button
                    variant="text"
                    onClick={handleCancelDetails}
                    disabled={detailsSaving || !hasDetailsChanged}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveDetails}
                    disabled={detailsSaving || !hasDetailsChanged}
                  >
                    {detailsSaving ? t('runners.profile.personalDetails.saving') : t('runners.profile.personalDetails.saveChanges')}
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      {!authLoading && Boolean(authUserId) ? (
        <Paper sx={{ mb: 4, p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t('runners.profile.upcoming.title')}
          </Typography>
          {canViewPrivateDetails
            ? (privateProfileLoaded
                ? renderUpcomingRegistrations(profile.upcomingRegistrations)
                : <Typography variant="body2" color="text.secondary">{t('common.loading')}</Typography>)
            : (publicUpcomingLoaded
                ? renderUpcomingRegistrations(profile.upcomingRegistrations)
                : <Typography variant="body2" color="text.secondary">{t('common.loading')}</Typography>)}
        </Paper>
      ) : null}

      <Paper sx={{ mb: 4 }}>
        <Button
          fullWidth
          onClick={() => setKutcExpanded((prev) => !prev)}
          sx={{
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            textTransform: 'none',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          endIcon={<ChevronDown size={20} style={{ transform: kutcExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
        >
          <Typography variant="h5" component="span">
            {t('runners.profile.kutc.historyTitle', { event: t('kutc.title') })}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary">
            {kutcExpanded ? t('runners.profile.kutc.hideHistory') : t('runners.profile.kutc.showHistory')}
          </Typography>
        </Button>
        <Collapse in={kutcExpanded} timeout="auto">
          <Divider />
          <Box sx={{ px: 3, py: 3 }}>
            {kutcLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {kutcError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {kutcError}
              </Alert>
            )}
            {!kutcLoading && !kutcError && kutcData && (
              <>
                <Paper
                  sx={{
                    p: 3,
                    mb: 4,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    boxShadow: '0 6px 18px rgba(25, 118, 210, 0.12)'
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.kutc.stats.totalAppearances')}
                        </Typography>
                        <Typography variant="h5">{kutcStats?.appearances ?? '—'}</Typography>
                        <Chip label={t('runners.profile.kutc.stats.years', { years: kutcStats?.years ?? '—' })} size="small" />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.kutc.stats.totalLoops')}
                        </Typography>
                        <Typography variant="h5">{kutcStats?.totalLoops ?? '—'}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.kutc.stats.bestPerformance')}
                        </Typography>
                        <Typography variant="h6">{kutcStats?.best ?? '—'}</Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
                {kutcData.participations.length > 0 && (
                  <Box>
                    {renderParticipations(kutcData.participations)}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Button
          fullWidth
          onClick={() => {
            setMoExpanded((prev) => !prev);
          }}
          sx={{
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            textTransform: 'none',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          endIcon={<ChevronDown size={20} style={{ transform: moExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
        >
          <Typography variant="h5" component="span">
            {t('runners.profile.mo.historyTitle', { event: t('mo.title') })}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary">
            {moExpanded ? t('runners.profile.mo.hideHistory') : t('runners.profile.mo.showHistory')}
          </Typography>
        </Button>
        <Collapse in={moExpanded} timeout="auto">
          <Divider />
          <Box sx={{ px: 3, py: 3 }}>
            {moLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!moLoading && (
              <>
                <Paper
                  sx={{
                    p: 3,
                    mb: 2,
                    border: '2px solid',
                    borderColor: 'success.main',
                    backgroundColor: 'rgba(46, 125, 50, 0.05)',
                    boxShadow: '0 6px 18px rgba(46, 125, 50, 0.12)'
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.mo.stats.totalAppearances')}
                        </Typography>
                        <Typography variant="h5">{t('runners.profile.mo.appearancesCount', { count: moSummary?.appearances ?? 0 })}</Typography>
                        <Chip label={t('runners.profile.mo.stats.years', { years: formatYears(moSummary?.years ?? []) })} size="small" />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.mo.stats.bestTimeGender')}
                        </Typography>
                        <Typography variant="h6">{moSummary?.bestTimeDisplay ?? '—'} {moSummary?.bestGenderRank ? `• #${moSummary.bestGenderRank}` : ''}</Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          {t('runners.profile.mo.stats.bestAdjustedAgg')}
                        </Typography>
                        <Typography variant="h6">{moSummary?.bestAdjustedDisplay ?? '—'} {moSummary?.bestAggRank ? `• #${moSummary.bestAggRank}` : ''}</Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
                {hasMoAppearances && (
                  <Box>
                    {renderMoParticipation(moResults)}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Unclaim Confirmation Dialog */}
      <Dialog
        open={cancellationDialogOpen}
        onClose={resetCancellationDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('runners.profile.upcoming.cancel.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('runners.profile.upcoming.cancel.dialogDescription')}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('runners.profile.upcoming.cancel.irreversibleNotice')}
          </Alert>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('runners.profile.upcoming.cancel.reasonLabel')}</InputLabel>
            <Select
              label={t('runners.profile.upcoming.cancel.reasonLabel')}
              value={selectedCancellationReasonCode}
              onChange={(event) => setSelectedCancellationReasonCode(String(event.target.value || DEFAULT_CANCELLATION_REASON_CODE))}
            >
              {cancellationReasonOptions.map((reason) => (
                <MenuItem key={reason.id || reason.code} value={reason.code}>
                  {reason.verboseName || reason.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            minRows={3}
            multiline
            value={selectedCancellationReasonText}
            onChange={(event) => setSelectedCancellationReasonText(event.target.value)}
            label={t('runners.profile.upcoming.cancel.reasonTextLabel')}
            placeholder={t('runners.profile.upcoming.cancel.reasonTextPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCancellationDialogs}>{t('common.cancel')}</Button>
          <Button onClick={handleCancellationContinue} color="warning" variant="contained">
            {t('runners.profile.upcoming.cancel.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancellationFinalDialogOpen}
        onClose={resetCancellationDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('runners.profile.upcoming.cancel.finalTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('runners.profile.upcoming.cancel.irreversibleNotice')}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('runners.profile.upcoming.cancel.finalDescription')}
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={cancellationAcknowledged}
                onChange={(event) => setCancellationAcknowledged(event.target.checked)}
              />
            )}
            label={t('runners.profile.upcoming.cancel.confirmIrreversible')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancellationBack} disabled={Boolean(cancellingRegistrationId)}>
            {t('common.back')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!cancellationAcknowledged || Boolean(cancellingRegistrationId)}
            onClick={handleConfirmCancellation}
          >
            {cancellingRegistrationId ? <CircularProgress size={16} color="inherit" /> : t('runners.profile.upcoming.cancel.confirmAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unclaimDialogOpen}
        onClose={() => setUnclaimDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('runnerProfile.unclaimDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('runnerProfile.unclaimDialog.description')}
          </Typography>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              {t('runnerProfile.unclaimDialog.keepDetailsQuestion')}
            </FormLabel>
            <RadioGroup
              value={keepPersonalDetails ? 'keep' : 'clear'}
              onChange={(e) => setKeepPersonalDetails(e.target.value === 'keep')}
            >
              <FormControlLabel
                value="keep"
                control={<Radio />}
                label={t('runnerProfile.unclaimDialog.keepDetails')}
              />
              <FormControlLabel
                value="clear"
                control={<Radio />}
                label={t('runnerProfile.unclaimDialog.clearDetails')}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnclaimDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleUnclaimConfirm} 
            color="warning" 
            variant="contained"
          >
            {t('runnerProfile.unclaimDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RunnerProfilePage;
